import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Buildings,
  MapPin,
  ArrowRight,
  Boat,
  Airplane,
  Truck,
  MagnifyingGlass,
  Eye,
  Pencil,
  Trash,
  CheckSquare,
  Square,
  TrendUp,
  Spinner,
  Package,
} from '@phosphor-icons/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import { formatDateBR, daysUntil } from '../../lib/date'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

interface MyOffer {
  id: string
  status: string
  currency: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  breakdown: any
  paymentTerms: string
  deliveryTime: number
  validityDate: string
  notes: string
  createdAt: string
  updatedAt: string
  sentAt: string | null
  acceptedAt: string | null
  declinedAt: string | null
  declinedReason: string | null
  quoteId: string
  quoteTxControl: string
  quoteStatus: string
  originCity: string
  originCountry: string
  destinationCity: string
  destinationCountry: string
  modalityName: string
  modalityType: string
  shipperName: string
  orderId: string | null
}

export default function MyOffersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showError, confirm } = useDialog()

  const [loading, setLoading] = useState(true)
  const [offers, setOffers] = useState<MyOffer[]>([])
  const [error, setError] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchParams] = useSearchParams()
  // Plane #15: the "Propostas Aprovadas" menu links here with ?status=accepted
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'active')
  const [selectedOffers, setSelectedOffers] = useState<string[]>([])

  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20

  useEffect(() => {
    fetchOffers()
  }, [page, filterStatus])

  // Keep the filter in sync with the URL (?status=) when navigating via menu
  useEffect(() => {
    const s = searchParams.get('status')
    if (s) setFilterStatus(s)
  }, [searchParams])

  async function fetchOffers() {
    try {
      setLoading(true)
      setError(null)

      const queryParams = new URLSearchParams()
      queryParams.set('page', String(page))
      queryParams.set('limit', String(limit))
      if (filterStatus !== 'all') queryParams.set('status', filterStatus)

      const res = await api.get(`/freight-forwarder/offers?${queryParams.toString()}`)

      setOffers(res.data.offers)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch (err: any) {
      console.error('Error fetching offers:', err)
      setError(err.response?.data?.error || 'Erro ao carregar propostas')
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteOffer(offerId: string) {
    const confirmed = await confirm({
      title: 'Excluir Proposta',
      message: 'Tem certeza que deseja excluir esta proposta?',
      variant: 'danger',
      confirmText: 'Sim, Excluir',
      cancelText: 'Cancelar'
    })
    if (!confirmed) return

    try {
      await api.delete(`/freight-forwarder/offers/${offerId}`)
      fetchOffers()
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao excluir proposta')
    }
  }

  // Filter functionality (client-side for search)
  const filteredOffers = offers.filter((offer) => {
    if (!searchQuery) return true
    const matchesSearch =
      offer.quoteTxControl?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      offer.shipperName?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedOffers.length === filteredOffers.length) {
      setSelectedOffers([])
    } else {
      setSelectedOffers(filteredOffers.map((o) => o.id))
    }
  }

  const toggleSelectOffer = (offerId: string) => {
    setSelectedOffers((prev) =>
      prev.includes(offerId)
        ? prev.filter((id) => id !== offerId)
        : [...prev, offerId]
    )
  }

  const isOfferSelected = (offerId: string) => selectedOffers.includes(offerId)
  const isAllSelected =
    filteredOffers.length > 0 && selectedOffers.length === filteredOffers.length

  // Get status badge
  const getOfferStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      declined: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700',
    }
    const labels: Record<string, string> = {
      draft: 'Rascunho',
      sent: 'Enviada',
      accepted: 'Aceita',
      declined: 'Recusada',
      expired: 'Expirada',
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || badges.draft}`}>
        {labels[status] || status}
      </span>
    )
  }

  // Get modality badge
  const getModalityBadge = (modalityName: string) => {
    const name = modalityName?.toLowerCase() || ''
    let icon = <Package size={12} />
    let color = 'text-gray-600 bg-gray-100'
    let label = modalityName || 'Outro'

    if (name.includes('mar')) {
      icon = <Boat size={12} weight="bold" />
      color = 'text-blue-600 bg-blue-100'
      label = 'Marítimo'
    } else if (name.includes('aer') || name.includes('air')) {
      icon = <Airplane size={12} weight="bold" />
      color = 'text-purple-600 bg-purple-100'
      label = 'Aéreo'
    } else if (name.includes('ter') || name.includes('rod')) {
      icon = <Truck size={12} weight="bold" />
      color = 'text-green-600 bg-green-100'
      label = 'Terrestre'
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {icon}
        {label}
      </span>
    )
  }

  // Format currency
  const formatCurrency = (value: number, currency: string) => {
    if (!value || isNaN(value)) return '-'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(value)
  }

  // Calculate days until expiry
  const getDaysUntilExpiry = (validUntil: string) => {
    if (!validUntil) return '-'
    const days = daysUntil(validUntil)
    if (days < 0) return 'Expirada'
    if (days === 0) return 'Expira hoje'
    if (days === 1) return '1 dia'
    return `${days} dias`
  }

  // Calculate metrics from loaded data
  const metrics = {
    overview: {
      total: total,
      pending: offers.filter((o) => o.status === 'sent').length,
      thisMonth: offers.filter(
        (o) => new Date(o.createdAt).getMonth() === new Date().getMonth()
      ).length,
    },
    performance: {
      accepted: offers.filter((o) => o.status === 'accepted').length,
      declined: offers.filter((o) => o.status === 'declined').length,
      acceptanceRate: (() => {
        const decided = offers.filter((o) => o.status === 'accepted' || o.status === 'declined').length
        const accepted = offers.filter((o) => o.status === 'accepted').length
        return decided > 0 ? Math.round((accepted / decided) * 100) : 0
      })(),
    },
    financial: {
      totalValue: offers.reduce((acc, o) => acc + (o.totalValue || 0), 0),
      avgOfferValue: offers.length > 0
        ? offers.reduce((acc, o) => acc + (o.totalValue || 0), 0) / offers.length
        : 0,
    },
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Minhas Propostas
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedOffers.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedOffers.length}</span>
                  selecionada{selectedOffers.length > 1 ? 's' : ''}
                </div>
              )}
              <Button
                onClick={() => navigate('/freight-forwarder/opportunities')}
                size="default"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <MagnifyingGlass size={18} weight="bold" />
                Ver Oportunidades
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Zone - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Overview Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Visão Geral
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{metrics.overview.pending}</p>
                <p className="text-sm text-gray-600">
                  Pendentes de {total} total
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  +{metrics.overview.thisMonth} este mês
                </p>
              </div>
              <FileText size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Performance Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Performance
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Taxa de aceitação</span>
                <span className="font-semibold text-emerald-600 flex items-center gap-1">
                  <TrendUp size={14} weight="bold" />
                  {metrics.performance.acceptanceRate}%
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Aceitas</span>
                <span className="font-semibold text-gray-900">
                  {metrics.performance.accepted}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Recusadas</span>
                <span className="font-semibold text-gray-900">
                  {metrics.performance.declined}
                </span>
              </div>
            </div>
          </div>

          {/* Financial Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Financeiro
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.financial.totalValue, 'BRL')}
                </p>
                <p className="text-sm text-gray-600">
                  Valor total em propostas
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Média: {formatCurrency(metrics.financial.avgOfferValue, 'BRL')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por cotação ou embarcador..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value)
                setPage(1)
              }}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="active">Ativas (Rascunho + Enviadas)</option>
              <option value="all">Todos Status</option>
              <option value="draft">Rascunho</option>
              <option value="sent">Enviadas</option>
              <option value="accepted">Aceitas</option>
              <option value="declined">Recusadas</option>
              <option value="expired">Expiradas</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando propostas...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => fetchOffers()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Data Grid */}
        {!loading && !error && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center w-full"
                      >
                        {isAllSelected ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cotação / Embarcador
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rota
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modalidade
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Validade
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enviada
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-20 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOffers.map((offer) => (
                    <tr
                      key={offer.id}
                      className={`bg-white hover:bg-gray-50 transition-colors ${
                        isOfferSelected(offer.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelectOffer(offer.id)}
                          className="flex items-center justify-center"
                        >
                          {isOfferSelected(offer.id) ? (
                            <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                          ) : (
                            <Square size={18} className="text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {offer.shipperName}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {offer.quoteTxControl}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[80px]">{offer.originCity}</span>
                          <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                          <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[80px]">{offer.destinationCity}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getModalityBadge(offer.modalityName)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end">
                          <MultiCurrencyTotal
                            totalsByCurrency={totalsFromLegacyOffer(offer)}
                            variant="compact"
                          />
                          {offer.deliveryTime && (
                            <span className="text-xs text-gray-500">
                              {offer.deliveryTime} dias
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {getDaysUntilExpiry(offer.validityDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getOfferStatusBadge(offer.status)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {offer.sentAt ? formatDateBR(offer.sentAt) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 sticky right-0 z-10 bg-inherit shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/freight-forwarder/offers/${offer.id}`)}
                            className="h-9 w-9 p-0"
                            title="Ver proposta"
                          >
                            <Eye size={20} className="text-gray-600" />
                          </Button>
                          {offer.status === 'accepted' && offer.orderId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/freight-forwarder/orders/${offer.orderId}`)}
                              className="h-9 px-2 gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Ver pedido"
                            >
                              <Package size={20} />
                              <span className="text-xs">Pedido</span>
                            </Button>
                          )}
                          {offer.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/freight-forwarder/offers/${offer.id}/edit`)}
                              className="h-9 w-9 p-0"
                              title="Editar proposta"
                            >
                              <Pencil size={20} className="text-gray-600" />
                            </Button>
                          )}
                          {offer.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOffer(offer.id)}
                              className="h-9 w-9 p-0 hover:text-red-600"
                              title="Excluir proposta"
                            >
                              <Trash size={20} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredOffers.length === 0 && !loading && (
              <div className="text-center py-12 bg-gray-50">
                <FileText size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                <p className="text-gray-500 text-sm">Nenhuma proposta encontrada</p>
              </div>
            )}

            {/* Footer with pagination */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{total}</span> propostas
                {selectedOffers.length > 0 && (
                  <>
                    {' '}
                    · <span className="font-medium">{selectedOffers.length}</span> selecionada
                    {selectedOffers.length > 1 ? 's' : ''}
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </AppLayout>
  )
}
